import {
    createI18n,
    LanguageCode,
} from '@gagovictor/shared-frontend-core/dist/i18n';
import { useMemo } from 'react';

import { useAdFree } from '../monetization/adFree';

import type { MessageKey } from './en-US/ui';
import { enUS } from './en-US/ui';
import { ptBR } from './pt-BR/ui';
import type { Locale, LocaleContent } from './types';

export type { MessageKey } from './en-US/ui';
export type { Locale } from './types';

/** The languages the app is translated into. The first is the fallback. */
export const LANGUAGES = [
    'en-US',
    'pt-BR',
] as const satisfies readonly LanguageCode[];
export type Language = (typeof LANGUAGES)[number];
const FALLBACK: Language = 'en-US';

/** The small UI catalogue of each language, always bundled: `t()` needs it before anything else. */
const UI: Record<Language, Record<MessageKey, string>> = {
    'en-US': enUS,
    'pt-BR': ptBR,
};

/** Fetches the heavy text (cards, readings, astrology) of a language. Each language is its own chunk. */
const FETCHERS: Record<Language, () => Promise<LocaleContent>> = {
    'en-US': () => import('./en-US/content').then((m) => m.enUSContent),
    'pt-BR': () => import('./pt-BR/content').then((m) => m.ptBRContent),
};

const content: Partial<Record<Language, LocaleContent>> = {};
const pending: Partial<Record<Language, Promise<LocaleContent>>> = {};

/** True once the readings of this language are in memory, so `localeFor` will not throw. */
export const isContentLoaded = (language: Language): boolean =>
    Boolean(content[language]);

/** Registers the text of a language that was imported statically (tests do this, so they need no waiting). */
export const registerContent = (
    language: Language,
    loaded: LocaleContent
): void => {
    content[language] = loaded;
};

/** Loads the readings of a language once; a failed fetch can be tried again. */
export const loadContent = (language: Language): Promise<LocaleContent> => {
    const have = content[language];
    if (have) return Promise.resolve(have);
    const inFlight = pending[language];
    if (inFlight) return inFlight;
    const request = FETCHERS[language]()
        .then((loaded) => {
            content[language] = loaded;
            return loaded;
        })
        .finally(() => {
            delete pending[language];
        });
    pending[language] = request;
    return request;
};

/**
 * All the text of one language: `.ui` for `t()`, `.cards`, `.daily`, `.lenses` and `.astro` for the readings.
 * Synchronous, so the language must have been loaded (`loadContent`; `LocaleGate` and the language menu do it).
 */
export const localeFor = (language: Language): Locale => {
    const loaded = content[language] ?? content[FALLBACK];
    if (!loaded) {
        throw new Error(`The text of ${language} has not been loaded yet`);
    }
    return { ui: UI[language] ?? UI[FALLBACK], ...loaded };
};

const base = createI18n<Language, MessageKey>({
    languages: LANGUAGES,
    fallback: FALLBACK,
    storageKey: 'arcana:language',
    catalogs: {
        'en-US': UI['en-US'],
        'pt-BR': UI['pt-BR'],
    },
});

export const { I18nProvider, translateIn, initialLanguage } = base;

/**
 * Copy about the video ("A short video opens each reading") is wrong once the lifetime unlock removes the
 * ads. A key `x` that has a sibling `xFree` reads as that sibling for an owner, so screens do not branch.
 */
const adFreeKey = (key: MessageKey, language: Language): MessageKey => {
    const free = `${key}Free` as MessageKey;
    return free in (UI[language] ?? UI[FALLBACK]) ? free : key;
};

export const useI18n = () => {
    const i18n = base.useI18n();
    const adFree = useAdFree();
    const { t: translate, language } = i18n;
    return useMemo(
        () => ({
            ...i18n,
            t: (key: MessageKey, vars?: Parameters<typeof translate>[1]) =>
                translate(adFree ? adFreeKey(key, language) : key, vars),
        }),
        // `i18n` changes whenever the language does.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [i18n, adFree]
    );
};

export const useT = () => useI18n().t;
